/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { Link } from "react-router-dom";
import { observer, inject } from "mobx-react";
// UI Frameworks
import { Form, Row, Col, Icon, Typography, Table, Alert, Select, Spin } from "antd";
import { FormComponentProps } from "antd/lib/form";
// Hooks and utilities
import { isEmpty } from "lodash";
import UserStore from "state/user";
// Shared components
import InputNumber from "components/InputNumber";
import Button from "components/Button";
import Box from "components/Box";
// Hooks and Utilities
import jMoment from "moment-jalaali";
import addCommas, { isFloat } from "helpers/index";
import { persianNumber } from "helpers/persian";
import useSettlement from "hooks/useSettlement";
import BankAccountService, { AccountModel } from "services/http/endpoints/bank-accounts";
import SettlementService, { SettlementType, SettlementStatus } from "services/http/endpoints/settlement";
// styles
import styles from "./index.module.scss";

const { Option } = Select;

interface IProps extends FormComponentProps {
	user: UserStore;
}

const FirstStep: React.FC<IProps> = (props: IProps) => {
	const [loading, setLoading] = React.useState(true);
	const [pagination, setPagination] = React.useState();
	const { requestSettlementBySheba } = useSettlement();
	const { getFieldDecorator, validateFields, resetFields } = props.form;
	const [settlements, setSettlement] = React.useState([] as SettlementType[]);
	const [bankAccounts, setBankAccounts] = React.useState([] as AccountModel[]);

	const fetchData = async (params?: any) => {
		try {
			setLoading(true);
			SettlementService.size = params.size;
			SettlementService.page = params.page;
			SettlementService.makeOrderBy("createdDate", false);
			const result = await SettlementService.search(SettlementService.getSearchFilter());
			const pager = { ...pagination };
			// Read total count from server
			pager.total = result.pagination.total;

			// fetch bank accounts
			BankAccountService.makeOrderBy("createdDate", false);
			BankAccountService.makeFilters("type", "EQ", "SHABA");
			BankAccountService.makeFilters("status", "EQ", "CONFIRMED");
			const accounts = await BankAccountService.search(BankAccountService.getSearchFilter());

			setLoading(false);
			setPagination(pager);
			setSettlement(result.result);
			setBankAccounts(accounts.result);
		} catch (error) {
			setLoading(false);
		}
	};

	// get facilities
	React.useEffect(() => {
		fetchData({ size: 10, page: 1 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();
		validateFields(async (err, values) => {
			if (!err && bankAccounts.length > 0) {
				setLoading(true);
				try {
					await requestSettlementBySheba(values);
					fetchData({ size: 10, page: 1 });
					resetFields(["amount", "cartId"]);
				} catch (e) {
					setLoading(false);
				}
				return;
			}
		});
	}

	function handleAmountValidator(_: any, value: string, callback: any) {
		if (!value) {
			callback("لطفا مقدار این فیلد را وارد کنید.");
		} else if (value && !Number(value)) {
			callback("لطفا مقدار عددی وارد کنید.");
		} else if (Number(value) < 0) {
			callback("مقدار منفی قابل قبول نیست.");
		} else if (isFloat(value)) {
			callback("لطفا مقدار اعشاری وارد نکنید.");
			return;
		} else {
			callback();
		}

		return;
	}

	function settlementStatusFormatted(status: string): string {
		switch (status) {
			case SettlementStatus.DONE:
				return "پرداخت شده";
			case SettlementStatus.FAIL:
				return "ناموفق";
			case SettlementStatus.INVALID_SHEBA:
				return "شماره شبا نامعتبر";
			case SettlementStatus.IN_PROGRESS:
				return "در حال انجام";
			case SettlementStatus.SUSPICIOUS:
				return "مشکوک";
			case SettlementStatus.TODO:
				return "در دست انجام";
			case SettlementStatus.UNKNOWN:
				return "نامشخص";
			default:
				return "-";
		}
	}

	function handleTableChange(_pagination: any, filters: any, sorter: any) {
		const pager = { ...pagination };
		pager.current = _pagination.current;
		setPagination({
			pagination: pager,
		});
		fetchData({
			size: _pagination.pageSize,
			page: _pagination.current,
		});
	}

	const columns = [
		{
			title: "ردیف",
			dataIndex: "key",
			key: "key",
			render: (key: number) => {
				return <span dir={"ltr"}>{key + 1}</span>;
			},
		},
		{
			title: "شماره شبا",
			dataIndex: "bankShabaNumber",
			key: "bankShabaNumber",
			render: (data: any) => {
				return <span className="ltr">{data}</span>;
			},
		},
		{
			title: "مبلغ درخواستی",
			dataIndex: "amount",
			key: "amount",
			render: (data: any) => {
				return <span>{addCommas(data)} ریال</span>;
			},
		},
		{
			title: "تاریخ درخواست",
			dataIndex: "createdDate",
			key: "createdDate",
			render: (data: any) => {
				return <span dir={"ltr"}>{jMoment(data).format("jYYYY/jMM/jDD")}</span>;
			},
		},
		{
			title: "وضعیت تسویه",
			dataIndex: "settlementStatus",
			key: "settlementStatus",
			render: (status: any) => {
				return <span>{settlementStatusFormatted(status)}</span>;
			},
		},
	];

	return (
		<div className="container">
			<div className={styles["settlement"]}>
				{loading && bankAccounts.length === 0 ? (
					<Spin />
				) : (
					<React.Fragment>
						<Row className={styles["issue-info"]} type="flex" justify="center">
							<Col md={10} xs={24}>
								<Box title="موجودی کیف پول شما" gray>
									<Row type="flex" justify="center" style={{ marginBottom: "10px" }}>
										<Col span={10}>
											{props.user.balance > -1 ? (
												<>{addCommas(props.user.balance)} ریال</>
											) : (
												"درحال بارگزاری..."
											)}
										</Col>
									</Row>
								</Box>
							</Col>
						</Row>
						<Row type="flex" justify="center">
							<Col md={12} xs={24}>
								<Typography.Paragraph strong>
									مبلغ مورد نظر خود را جهت تسویه وارد کنید.
								</Typography.Paragraph>
								<Form onSubmit={handleSubmit}>
									<Form.Item>
										{getFieldDecorator("amount", {
											rules: [
												{
													transform: value => persianNumber(value),
												},
												{
													validator: handleAmountValidator,
												},
											],
										})(
											<InputNumber
												disabled={bankAccounts.length === 0}
												formatter={(value: any) =>
													persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
												}
												parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
												autoComplete="off"
											/>,
										)}
									</Form.Item>
									{bankAccounts.length > 0 ? (
										<Form.Item>
											{getFieldDecorator("cartId", {
												initialValue: bankAccounts[0].id,
												rules: [
													{
														required: true,
														message: "لطفا شماره شبا را انتخاب کنید.",
													},
												],
											})(
												<Select
													showSearch
													style={{ width: 300 }}
													placeholder="انتخاب شماره شبا"
													optionFilterProp="children"
												>
													{bankAccounts.map((acc, key) => (
														<Option key={key} value={acc.id} className="ltr">
															IR{acc.value}
														</Option>
													))}
												</Select>,
											)}
										</Form.Item>
									) : (
										<Alert
											message="برای ثبت درخواست تسویه لطفا شماره شبا خود را ثبت کنید."
											description={
												<Link to="/dashboard/bank/accounts">
													<b>ثبت شماره شبا</b>
												</Link>
											}
											type="warning"
										/>
									)}
									<Form.Item>
										<Button
											htmlType="submit"
											loading={loading}
											disabled={bankAccounts.length === 0}
											green
										>
											ثبت
											<Icon
												type="arrow-left"
												style={{ verticalAlign: "middle", marginRight: "8px" }}
											/>
										</Button>
									</Form.Item>
								</Form>
							</Col>
						</Row>
					</React.Fragment>
				)}
				<Table
					bordered
					loading={loading}
					pagination={pagination}
					className={styles["table"]}
					onChange={handleTableChange}
					scroll={{ x: true, y: false }}
					dataSource={
						!isEmpty(settlements)
							? settlements.map((data: SettlementType, key: number) => ({
									key,
									id: data.id,
									amount: data.amount,
									bankShabaNumber: data.bankShabaNumber,
									settlementStatus: data.settlementStatus,
									createdDate: data.createdDate,
							  }))
							: []
					}
					columns={columns}
				/>
			</div>
		</div>
	);
};

export default Form.create({ name: "FirstStep" })(inject("user")(observer(FirstStep)));
